import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTitleBar } from "@/context/TitleBarProvider";
import { Server, Trash2, Unplug, LifeBuoy } from "lucide-react";
import { useAutomationServersQuery } from "@/hooks/useAutomationServersQuery";
import { DeleteAutomationServerModal } from "@/components/automation-server/DeleteAutomationServerModal";
import { DisasterRecoveryModal } from "@/components/automation-server/DisasterRecoveryModal";
import { ServerTypeTile } from "@/components/automation-server/ServerRow";

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 border-t py-3 first:border-t-0">
      <dt className="w-40 shrink-0 text-sm text-muted-foreground">{label}</dt>
      <dd className="min-w-0 flex-1 text-sm">{children}</dd>
    </div>
  );
}

const AutomationServerDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const { data: automationServersData } = useAutomationServersQuery();
  const { setTitle, setIcon, setButtons } = useTitleBar();
  const [recovering, setRecovering] = useState(false);

  const server = automationServersData?.pages.flatMap((page) => page.results).find(
    (server) => server.automation_server_id === id,
  );

  useEffect(() => {
    setTitle(server?.name ?? "Server");
    setIcon(<Server size={24} />);
    setButtons(
      <>
        {server?.bailey_url && !server.registration_pending && (
          <Button variant="outline" onClick={() => setRecovering(true)}>
            <LifeBuoy className="h-4 w-4 mr-1" />
            Recover from backups
          </Button>
        )}
        <DeleteAutomationServerModal
          serverName={server?.name ?? ""}
          serverId={server?.automation_server_id ?? ""}
          isCloud={server?.server_type === "hetzner"}
        >
          {server?.server_type === "hetzner" ? (
            <Button variant="destructive">
              <Trash2 className="h-4 w-4 mr-1" />
              Delete server
            </Button>
          ) : (
            <Button variant="outline">
              <Unplug className="h-4 w-4 mr-1" />
              Disconnect server
            </Button>
          )}
        </DeleteAutomationServerModal>
        {server?.bailey_url && (
          <Button asChild>
            <a href={server.bailey_url} target="_blank" rel="noopener noreferrer">Open Bailey console</a>
          </Button>
        )}
      </>
    );
  }, [server, setTitle, setButtons, setIcon]);

  if (!server) {
    return <div>Automation server not found</div>;
  }

  const cloud = server.server_type === "hetzner";

  return (
    <div className="space-y-6 py-4">
      <section className="rounded-xl border bg-card p-5">
        <div className="flex items-start gap-4">
          <ServerTypeTile cloud={cloud} />
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold leading-tight">{server.name}</h2>
            <p className="text-sm text-muted-foreground">{cloud ? "Private cloud server hosted by BitSwan" : "Your own machine, connected to the AOC"}</p>
          </div>
        </div>
        <dl className="mt-4">
          <Row label="Address">{server.domain ?? "—"}</Row>
          {server.ipv4_address && <Row label="IPv4">{server.ipv4_address}</Row>}
          {server.ipv6_address && <Row label="IPv6">{server.ipv6_address}</Row>}
          <Row label="Server id"><code className="text-xs">{server.automation_server_id}</code></Row>
          <Row label="Bailey">{server.bailey_version ? `${server.bailey_version}, reported ${server.bailey_version_reported_at ? new Date(server.bailey_version_reported_at).toLocaleString() : "at registration"}` : server.bailey_url ? "registered" : "not registered yet"}</Row>
          {cloud && <Row label="Size">{[server.hetzner_server_type, server.hetzner_datacenter].filter(Boolean).join(" in ") || "—"}</Row>}
          <Row label="Added">{new Date(server.created_at).toLocaleString()}</Row>
        </dl>
        <p className="mt-4 text-sm text-muted-foreground">
          Workspaces and automations are created and run in the Bailey console on the server itself.
        </p>
      </section>
      <DisasterRecoveryModal serverName={server.name} serverId={server.automation_server_id} open={recovering} onOpenChange={setRecovering} />
    </div>
  );
};

export default AutomationServerDetailPage;
